import { computed, isRef, type Ref } from "vue"
import { useDateTimeHelper } from "@/composables/dateTimeHelper"
import type { ChangeEvent, FieldValue, TemporalDisplayConfig } from "@/models/temporalTypes"

const SKIP_FIELDS = new Set(["sysStartTime", "sysEndTime", "id", "updatedTimeStamp", "createdTimeStamp", "updatedTimestamp", "createdTimestamp"])

export function useTemporalDiff(
	rawData: Ref<Record<string, any[]>>,
	displayConfig: Ref<TemporalDisplayConfig> | TemporalDisplayConfig = {}
) {
	const resolvedConfig = computed(() => (isRef(displayConfig) ? displayConfig.value : displayConfig))
	function resolvePersonName(personId: string | null | undefined): string {
		if (!personId) return "Unknown"
		const p = Object.values(rawData.value)
			.flat()
			.find((r) => r.id === personId)
		return p?.firstName && p?.lastName ? `${p.firstName} ${p.lastName}` : "Unknown"
	}

	function resolveLookupName(id: string, entityRows: any[], nameField: string): string | null {
		const row = entityRows?.find((r) => r.id === id)
		return row ? String(row[nameField] ?? "") || null : null
	}

	function findDisplayRows(entityKey: string, fkField: string): any[] | null {
		const nameField = resolvedConfig.value[entityKey]?.projections?.[fkField]?.nameField
		if (!nameField) return null

		const baseKey = fkField.replace(/Id$/i, "")
		const entry = Object.entries(rawData.value).find(
			([k]) => k.toLowerCase() === baseKey.toLowerCase() || k.toLowerCase() === baseKey.toLowerCase() + "s"
		)
		return entry ? entry[1] : null
	}

	function buildProjectionFields(entityKey: string, record: any, onlyAlwaysShow: boolean): FieldValue[] {
		const projections = resolvedConfig.value[entityKey]?.projections ?? {}
		return Object.entries(projections)
			.filter(([, display]) => !onlyAlwaysShow || display.alwaysShow)
			.flatMap(([fkField, display]) => {
				const id = record[fkField]
				if (!id) return []
				// Try name-convention lookup first (e.g. emissionFactorId → emissionFactor),
				// then fall back to searching all display collections for a row matching the ID.
				// The fallback is needed when multiple FK fields resolve to the same entity type
				// (e.g. developmentStageId and productNameId both resolve to masterDropdown).
				let rows = findDisplayRows(entityKey, fkField)
				if (!rows) {
					const entry = Object.entries(rawData.value).find(([, rowList]) => Array.isArray(rowList) && (rowList as any[]).some((r) => r.id === id))
					rows = entry ? (entry[1] as any[]) : null
				}
				if (!rows) return []
				const resolved = resolveLookupName(id, rows, display.nameField)
				if (!resolved) return []
				return [{ field: display.label, value: resolved, alwaysShow: display.alwaysShow ?? false }]
			})
	}

	function buildRegularFields(entityKey: string, record: any): FieldValue[] {
		const allowedFields = resolvedConfig.value[entityKey]?.fields
		return Object.entries(record)
			.filter(([k]) => !SKIP_FIELDS.has(k) && !k.endsWith("Id"))
			.filter(([k]) => !allowedFields || allowedFields.includes(k))
			.filter(([, v]) => v !== null && v !== undefined)
			.map(([field, value]) => ({ field, value }))
	}

	function buildChanges(entityKey: string, current: any, previous: any) {
		const allowedFields = resolvedConfig.value[entityKey]?.fields
		return Object.entries(current)
			.filter(([k]) => !SKIP_FIELDS.has(k) && !k.endsWith("Id"))
			.filter(([k]) => !allowedFields || allowedFields.includes(k))
			.filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(previous[k]))
			.map(([field, to]) => ({ field, from: previous[field], to }))
	}

	function buildProjectionChanges(entityKey: string, current: any, previous: any) {
		const projections = resolvedConfig.value[entityKey]?.projections ?? {}
		return Object.entries(projections).flatMap(([fkField, display]) => {
			const currentId = current[fkField]
			const previousId = previous[fkField]
			if (currentId === previousId) return []
			let rows = findDisplayRows(entityKey, fkField)
			if (!rows) {
				const entry = Object.entries(rawData.value).find(
					([, rowList]) => Array.isArray(rowList) && (rowList as any[]).some((r: any) => r.id === currentId || r.id === previousId)
				)
				rows = entry ? (entry[1] as any[]) : null
			}
			if (!rows) return []
			const fromName = previousId ? (resolveLookupName(previousId, rows, display.nameField) ?? previousId) : null
			const toName = currentId ? (resolveLookupName(currentId, rows, display.nameField) ?? currentId) : null
			return [{ field: display.label, from: fromName, to: toName }]
		})
	}

	function isDeletedRow(r: Record<string, unknown>): boolean {
		const end = r["sysEndTime"]
		if (!end) return false
		return new Date(end as string).getFullYear() < 9999
	}

	function isSoftDeleteTransition(current: Record<string, unknown>, previous: Record<string, unknown>): boolean {
		return previous["isActive"] === true && current["isActive"] === false
	}

	function buildEventFields(entityKey: string, record: Record<string, unknown>): FieldValue[] {
		return [...buildProjectionFields(entityKey, record, false), ...buildRegularFields(entityKey, record)]
	}

	const changeEvents = computed<ChangeEvent[]>(() => {
		const events: ChangeEvent[] = []

		for (const [entityKey, records] of Object.entries(rawData.value)) {
			// Rows with no sysStartTime came in via a non-temporal projection lookup (e.g. a
			// self-referencing FK), not as a real version of this entity, so they aren't timeline events.
			const versionedRecords = records.filter((r) => r["sysStartTime"] != null)
			if (versionedRecords.length === 0) continue

			const label = resolvedConfig.value[entityKey]?.label ?? formatEntityLabel(entityKey)
			const getTimestamp = (r: Record<string, unknown>) => r["sysStartTime"] as string

			const byId = versionedRecords.reduce<Record<string, any[]>>((acc, r) => {
				const id = String(r["id"])
				;(acc[id] ??= []).push(r)
				return acc
			}, {})

			for (const idRecords of Object.values(byId)) {
				const sorted = [...idRecords].sort((a, b) => new Date(getTimestamp(a)).getTime() - new Date(getTimestamp(b)).getTime())

				for (let i = 0; i < sorted.length; i++) {
					const current = sorted[i]
					const personId = current["updatedByPersonId"] ?? current["createdByPersonId"]
					const personName = resolvePersonName(personId as string)
					const timestamp = getTimestamp(current)
					if (i === 0) {
						const fields = buildEventFields(entityKey, current)
						events.push({ entityKey, label, eventType: "created", personName, timestamp, fields, changes: [] })
					} else {
						const previous = sorted[i - 1]
						if (isSoftDeleteTransition(current, previous)) {
							events.push({ entityKey, label, eventType: "deleted", personName, timestamp, fields: buildEventFields(entityKey, current), changes: [] })
						} else {
							const changes = [...buildChanges(entityKey, current, previous), ...buildProjectionChanges(entityKey, current, previous)]
							if (changes.length > 0) {
								const fields = buildProjectionFields(entityKey, current, true)
								events.push({ entityKey, label, eventType: "updated", personName, timestamp, fields, changes })
							}
						}
					}
				}

				const lastRow = sorted[sorted.length - 1]
				const lastRowIsSoftDeleted = sorted.length > 1 && isSoftDeleteTransition(lastRow, sorted[sorted.length - 2])
				if (!lastRowIsSoftDeleted && isDeletedRow(lastRow)) {
					const timestamp = lastRow["sysEndTime"] as string
					const fields = buildEventFields(entityKey, lastRow)
					events.push({ entityKey, label, eventType: "deleted", personName: "", timestamp, fields, changes: [] })
				}
			}
		}

		return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
	})

	return { changeEvents }
}

export function formatDateTime(value: unknown): string {
	if (!value) return "-"
	const result = useDateTimeHelper(value as Date | string).toLocalTimeZoneStandardFormat()
	return result || "-"
}

export function formatEntityLabel(entityKey: string): string {
	const spaced = entityKey.replace(/([A-Z])/g, " $1")
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function formatFieldLabel(field: string): string {
	const spaced = field.replace(/([A-Z])/g, " $1")
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function formatValue(value: unknown): string {
	if (value === null || value === undefined) return "-"
	if (typeof value === "boolean") return value ? "Yes" : "No"
	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value)
	return String(value)
}
